import { Fragment, PureComponent, createRef } from 'react';

// Authentication provider
import { withAuth } from 'services/auth';

// Authentication provider
import { withRouter } from 'react-router-dom';

// Snackbar provider
import { withSnackbar } from 'notistack';

// Material UI components / helpers
import {
  Paper,
  Grid,
  Button,
  Divider,
  withStyles,
  TextField
} from '@material-ui/core';

// Mateirla UI Lab
import { Alert } from '@material-ui/lab';

// Luxon date helpers
import { DateTime } from 'luxon';

// To handle error messages
import { getErrorMessage } from 'helpers';

// Export cleanup
import compose from 'recompose/compose';

// Async status
import {
  AsyncStatus,
  BookingCard,
  DateRangePicker,
  TabContainer,
  GroupSessionBookingHistoryCard
} from 'components';
// Submit feedback dialog
import { SubmitFeedbackDialog } from '../..';

// Component styles
import styles from './styles';
import moment from 'moment-timezone';

class HistoryBookingsView extends PureComponent {
  constructor(props) {
    super(props);
    // Initialize the state
    this.state = {
      bookings: null,
      error: null,
      groupSessions: null,
      groupSessionsError: null,
      groupSessionsLoading: false,
      bookingsLoading: false,
      groupSessionsBookings: null,
      oneOnOneDateRange: [
        DateTime.local().minus({ months: 1 }),
        DateTime.local()
      ],
      groupSessionDateRange: [
        DateTime.local().minus({ months: 1 }),
        DateTime.local()
      ],
      hasAppliedOneOnOneFilter: false,
      hasAppliedGroupFilter: false,
      hasInitiallyLoaded: false,
      hasInitiallyLoadedGroup: false
    };
    this.cancelConfirmDialog = createRef();
    this.submitFeedbackDialog = createRef();
  }

  componentDidMount() {
    this.fetchBookings(null, false, false);
  }

  fetchBookings = async (
    dateRange = null,
    isGroupSession = false,
    store = false // Changed default to false
  ) => {
    try {
      const { auth } = this.props;
      const [startDate, endDate] = dateRange || [
        DateTime.local().minus({ months: 1 }),
        DateTime.local()
      ];

      // console.log(
      //   `%c[DEBUG] Fetching bookings (${
      //     isGroupSession ? 'Group' : 'One-on-One'
      //   })`,
      //   'color: blue; font-weight: bold;'
      // );
      // console.log('Start Date:', startDate.toISO(), startDate.toMillis());
      // console.log('End Date  :', endDate.toISO(), endDate.toMillis());
      // console.log('Store Flag:', store);

      if (isGroupSession) {
        this.setState({ groupSessionsLoading: true });

        const groupSessionsData =
          await auth.api.groupSessions.getGroupSessionsHistory(
            startDate.startOf('day').toMillis(),
            endDate.endOf('day').toMillis(),
            store
          );

        // console.log('[DEBUG] Group sessions API response:', groupSessionsData);

        if (groupSessionsData && groupSessionsData.length > 0) {
          groupSessionsData.forEach((session) => {
            session.bookings.forEach((booking) => {
              booking.id = session.id;
            });
          });

          // Sort group sessions by latest booking date
          const sortedGroupSessions = groupSessionsData.sort((a, b) => {
            const aLatest = Math.max(...a.bookings.map((bk) => bk.when || 0));
            const bLatest = Math.max(...b.bookings.map((bk) => bk.when || 0));
            return bLatest - aLatest;
          });

          const allBookings = sortedGroupSessions
            .flatMap((session) => session.bookings)
            .sort((a, b) => (b.when || 0) - (a.when || 0)); // Sort all bookings descending

          this.setState({
            groupSessions: sortedGroupSessions,
            groupSessionsError: null,
            groupSessionsLoading: false,
            groupSessionsBookings: allBookings,
            hasInitiallyLoadedGroup: true
          });
        } else {
          this.setState({
            groupSessions: [],
            groupSessionsError: null,
            groupSessionsLoading: false,
            groupSessionsBookings: [],
            hasInitiallyLoadedGroup: true
          });
        }
      } else {
        this.setState({ bookingsLoading: true });

        const data = await auth.api.bookings.getMyBookingHistory(
          startDate.startOf('day').toMillis(),
          endDate.endOf('day').toMillis(),
          store
        );

        console.log('[DEBUG] One-on-One API response:', data);

        const filteredBookings = data.bookings
          ? data.bookings
              .filter(
                (booking) =>
                  !['pending', 'in-progress', 'booked'].includes(booking.status)
              )
              .sort((a, b) => b.when - a.when) // Sort by date descending (latest first)
          : [];

        this.setState({
          bookings: filteredBookings,
          error: null,
          bookingsLoading: false,
          hasInitiallyLoaded: true
        });
      }
    } catch (error) {
      console.error('[DEBUG] fetchBookings error:', error);
      this.setState({
        error: isGroupSession ? null : getErrorMessage(error),
        groupSessionsError: isGroupSession ? getErrorMessage(error) : null,
        groupSessionsLoading: false,
        bookingsLoading: false,
        hasInitiallyLoaded: !isGroupSession
          ? true
          : this.state.hasInitiallyLoaded,
        hasInitiallyLoadedGroup: isGroupSession
          ? true
          : this.state.hasInitiallyLoadedGroup
      });
    }
  };

  onSubmitFeedback = async (booking, values) => {
    const { auth, enqueueSnackbar } = this.props;
    try {
      enqueueSnackbar('Submitting feedback...');
      const isPte =
        (booking?.tutor?.isPte ?? booking?.profile?.tutor?.isPte) &&
        booking?.info?.topic?.material === 'pte';

      await auth.api.bookings.submitFeedback(booking.id, values, isPte);
      this.setState({
        bookings: this.state.bookings.map((stateBooking) =>
          booking.id === stateBooking.id
            ? { ...stateBooking, status: 'completed', feedback: values }
            : stateBooking
        )
      });
      enqueueSnackbar('Feedback submitted!', { variant: 'success' });
    } catch (error) {
      console.error(error);
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    }
  };

  onSubmitFeedbackGrouSession = async (booking, values) => {
    const { auth, enqueueSnackbar } = this.props;
    try {
      enqueueSnackbar('Submitting feedback...');

      const { ...rest } = values;
      const newValues = { ...rest };

      await auth.api.groupSessions.submitFeedback(
        booking.id,
        booking?.bookingId,
        newValues
      );

      const currentgroupSession = this.state.groupSessions.find(
        (session) => session.id === booking.id
      );
      const updatedBookings = currentgroupSession.bookings.map((b) => {
        const presentStudents = b.students.filter((studnt) => {
          const present = studnt.status !== 'student-absent';
          return present.id;
        });
        if (b.bookingId === booking.bookingId) {
          const updatedStudents = b.students.map((studnt) => {
            if (presentStudents.includes(studnt.id)) {
              return {
                ...studnt,
                feedback: newValues
              };
            }
            return studnt;
          });

          const allStudentsHaveFeedback = updatedStudents.some(
            (stu) => stu.feedback
          );

          return {
            ...b,
            students: updatedStudents,
            status: allStudentsHaveFeedback ? 'completed' : 'pending-feedback'
          };
        }
        return b;
      });

      this.setState({
        groupSessions: this.state.groupSessions.map((stateBooking) => {
          if (booking.id === stateBooking.id) {
            return { ...stateBooking, bookings: updatedBookings };
          }
          return stateBooking;
        })
      });

      enqueueSnackbar('Feedback submitted!', { variant: 'success' });
    } catch (error) {
      console.error(error);
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    }
  };

  retryAPICall = (isGroupSession = false) => {
    if (isGroupSession) {
      this.setState({
        groupSessions: null,
        groupSessionsError: null,
        hasInitiallyLoadedGroup: false
      });
      this.fetchBookings(this.state.groupSessionDateRange, true, false);
    } else {
      this.setState({
        bookings: null,
        error: null,
        hasInitiallyLoaded: false
      });
      this.fetchBookings(this.state.oneOnOneDateRange, false, false);
    }
  };

  gotoSchedule = (searchType = null) => {
    const { auth, history, onUpdateTab } = this.props;
    if (auth.role === 'student') {
      onUpdateTab(0, searchType); // Pass searchType to open specific search tab
    } else {
      history.push('/tutor/schedule');
    }
  };

  onDateChanged = (dateRange, isGroupSession) => {
    if (isGroupSession) {
      this.setState({ groupSessionDateRange: dateRange });
    } else {
      this.setState({ oneOnOneDateRange: dateRange });
    }
  };

  onApplyFilter = (isGroupSession) => {
    const { oneOnOneDateRange, groupSessionDateRange } = this.state;

    const dateRange = isGroupSession
      ? groupSessionDateRange
      : oneOnOneDateRange;
    const [startDate, endDate] = dateRange;

    if (endDate < startDate) {
      this.props.enqueueSnackbar('End date cannot be before start date', {
        variant: 'error'
      });
      return;
    }

    if (isGroupSession) {
      this.setState({
        groupSessions: null,
        groupSessionsError: null,
        hasAppliedGroupFilter: true,
        hasInitiallyLoadedGroup: false
      });
      this.fetchBookings(dateRange, true, true);
    } else {
      this.setState({
        bookings: null,
        error: null,
        hasAppliedOneOnOneFilter: true,
        hasInitiallyLoaded: false
      });
      this.fetchBookings(dateRange, false, true);
    }
  };

  handleTabChange = (index) => {
    if (index === 0 && !this.state.bookings && !this.state.hasInitiallyLoaded) {
      this.fetchBookings(this.state.oneOnOneDateRange, false, false);
    } else if (
      index === 1 &&
      !this.state.groupSessions &&
      !this.state.hasInitiallyLoadedGroup
    ) {
      this.fetchBookings(this.state.groupSessionDateRange, true, false);
    }
  };

  render() {
    const {
      bookings,
      error,
      groupSessionsError,
      groupSessions,
      bookingsLoading,
      groupSessionsLoading,
      groupSessionsBookings,
      oneOnOneDateRange,
      groupSessionDateRange,
      hasAppliedOneOnOneFilter,
      hasAppliedGroupFilter,
      hasInitiallyLoaded,
      hasInitiallyLoadedGroup
    } = this.state;

    console.log({ groupSessionsBookings });
    const { classes, auth, onViewFeedback } = this.props;

    const tabs = [
      {
        label: 'One-on-One Lessons',
        component: (
          <>
            {auth.role === 'tutor' && (
              <SubmitFeedbackDialog
                ref={this.submitFeedbackDialog}
                onConfirm={this.onSubmitFeedback}
                type={null}
              />
            )}
            <Alert
              className={classes.reminderAlert}
              severity="info"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => this.retryAPICall(false)}>
                  Refresh Bookings
                </Button>
              }>
              <b>Reminder: </b>
              {auth.role === 'tutor'
                ? 'Click on student profiles to view feedback history'
                : 'Click on teacher photos to navigate to their profile'}
            </Alert>
            <Paper className={classes.dateControls}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={5} md={3}>
                  <TextField
                    type="date"
                    fullWidth
                    value={oneOnOneDateRange[0].toISODate()}
                    onChange={(e) =>
                      this.onDateChanged(
                        [
                          DateTime.fromISO(e.target.value),
                          oneOnOneDateRange[1]
                        ],
                        false
                      )
                    }
                  />
                </Grid>
                <Grid item xs={5} md={3}>
                  <TextField
                    type="date"
                    fullWidth
                    value={oneOnOneDateRange[1].toISODate()}
                    onChange={(e) =>
                      this.onDateChanged(
                        [
                          oneOnOneDateRange[0],
                          DateTime.fromISO(e.target.value)
                        ],
                        false
                      )
                    }
                  />
                </Grid>
                <Grid item xs={2} md={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={() => this.onApplyFilter(false)}
                    disabled={bookingsLoading}>
                    Apply
                  </Button>
                </Grid>
              </Grid>
            </Paper>

            {bookingsLoading ? (
              <AsyncStatus loading />
            ) : error ? (
              <AsyncStatus
                error={error}
                onRetry={() => this.retryAPICall(false)}
                retry
              />
            ) : hasInitiallyLoaded &&
              bookings &&
              bookings.length === 0 &&
              hasAppliedOneOnOneFilter ? (
              <AsyncStatus
                error="No bookings found for time period"
                onRetry={this.gotoSchedule}
                retryText={`go to ${
                  auth.role === 'student' ? 'bookings search' : 'schedule'
                }`}
                retrySize="medium"
                textVariant="h4"
                retry
              />
            ) : (
              <Grid container spacing={2}>
                {bookings
                  ?.sort((a, b) => {
                    // Sort by full date with year in descending order (most recent to oldest)
                    const aTime = a.when ? Number(a.when) : 0;
                    const bTime = b.when ? Number(b.when) : 0;
                    return bTime - aTime; // Descending order (end date to start date - most recent first)
                  })
                  .map((booking, index) => (
                    <Fragment key={booking.id}>
                      <Grid item xs={12}>
                        <BookingCard
                          auth={auth}
                          booking={booking}
                          history
                          onSubmitFeedback={() => {
                            if (auth.role === 'tutor')
                              this.submitFeedbackDialog.current.open(booking);
                          }}
                          onViewFeedback={(bookingFeedback) => {
                            onViewFeedback(bookingFeedback);
                          }}
                        />
                      </Grid>
                      {index !== bookings.length - 1 && (
                        <Grid key={booking.id} item xs={12}>
                          <Divider />
                        </Grid>
                      )}
                    </Fragment>
                  ))}
              </Grid>
            )}
          </>
        )
      },
      {
        label: 'Group Sessions',
        component: (
          <>
            {auth.role === 'tutor' && (
              <SubmitFeedbackDialog
                ref={this.submitFeedbackDialog}
                onConfirm={this.onSubmitFeedbackGrouSession}
                type="group"
              />
            )}
            <Paper className={classes.dateControls}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={5} md={3}>
                  <TextField
                    type="date"
                    fullWidth
                    value={groupSessionDateRange[0].toISODate()}
                    onChange={(e) =>
                      this.onDateChanged(
                        [
                          DateTime.fromISO(e.target.value),
                          groupSessionDateRange[1]
                        ],
                        true
                      )
                    }
                  />
                </Grid>
                <Grid item xs={5} md={3}>
                  <TextField
                    type="date"
                    fullWidth
                    value={groupSessionDateRange[1].toISODate()}
                    onChange={(e) =>
                      this.onDateChanged(
                        [
                          groupSessionDateRange[0],
                          DateTime.fromISO(e.target.value)
                        ],
                        true
                      )
                    }
                  />
                </Grid>
                <Grid item xs={2} md={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={() => this.onApplyFilter(true)}
                    disabled={groupSessionsLoading}>
                    Apply
                  </Button>
                </Grid>
              </Grid>
            </Paper>

            {groupSessionsLoading ? (
              <AsyncStatus loading />
            ) : groupSessionsError ? (
              <AsyncStatus
                error={groupSessionsError}
                onRetry={() => this.retryAPICall(true)}
                retry
              />
            ) : hasInitiallyLoadedGroup &&
              (groupSessions === null || groupSessions?.length === 0) &&
              hasAppliedGroupFilter ? (
              <AsyncStatus
                error="No bookings found for time period"
                onRetry={() => this.gotoSchedule('group')}
                retryText={`go to ${
                  auth.role === 'student' ? 'Group Sessions Search' : 'schedule'
                }`}
                retrySize="medium"
                textVariant="h4"
                retry
              />
            ) : (
              <Grid container spacing={2}>
                {groupSessions && groupSessions.length > 0 ? (
                  groupSessions
                    .sort((a, b) => {
                      // Sort by latest booking's when (date with year & time) in descending order
                      // Get the latest booking date from each session's bookings array
                      const getLatestBookingTime = (session) => {
                        if (!session.bookings || session.bookings.length === 0)
                          return 0;
                        const bookingTimes = session.bookings
                          .map((booking) => {
                            if (!booking.when) return 0;
                            if (typeof booking.when === 'string') {
                              return new Date(booking.when).getTime();
                            }
                            if (typeof booking.when?.toMillis === 'function') {
                              return booking.when.toMillis();
                            }
                            return Number(booking.when) || 0;
                          })
                          .filter((time) => time > 0);
                        return bookingTimes.length > 0
                          ? Math.max(...bookingTimes)
                          : 0;
                      };

                      const aTime = getLatestBookingTime(a);
                      const bTime = getLatestBookingTime(b);
                      return bTime - aTime; // Descending order (latest first)
                    })
                    .map((session, index) => (
                      <Fragment key={session.id || index}>
                        <Grid item xs={12}>
                          <GroupSessionBookingHistoryCard
                            groupSession={{
                              ...session,
                              bookings: session.bookings || [] // Ensure bookings is always an array
                            }}
                            auth={auth}
                            onSubmitFeedback={(b) => {
                              if (auth.role === 'tutor')
                                this.submitFeedbackDialog.current.open(b);
                            }}
                            onViewFeedback={(bookingFeedback) => {
                              onViewFeedback(bookingFeedback);
                            }}
                          />
                        </Grid>
                      </Fragment>
                    ))
                ) : (
                  <AsyncStatus
                    error="No bookings found for time period"
                    onRetry={this.gotoSchedule}
                    retryText={`go to ${
                      auth.role === 'student' ? 'bookings Search' : 'schedule'
                    }`}
                    retrySize="medium"
                    textVariant="h4"
                    retry
                  />
                )}
              </Grid>
            )}
          </>
        )
      }
    ];

    // Return a rendered list of the items.
    return (
      <TabContainer
        tabs={tabs}
        initial={0}
        view={false}
        onChange={this.handleTabChange}
      />
    );
  }
}

export default compose(
  withSnackbar,
  withRouter,
  withAuth,
  withStyles(styles)
)(HistoryBookingsView);
